import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { Subscription } from '@/types'
import LoadingSpinner from '@/components/common/LoadingSpinner'

interface SubscriptionFormProps {
  isOpen: boolean
  editingSubscription: Subscription | null
  saving: boolean
  error: string | null
  onSave: (values: {
    email: string
    notify_info: boolean
    notify_warn: boolean
    notify_error: boolean
    enabled: boolean
  }) => void
  onCancel: () => void
}

export default function SubscriptionForm({
  isOpen,
  editingSubscription,
  saving,
  error,
  onSave,
  onCancel,
}: SubscriptionFormProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [notifyInfo, setNotifyInfo] = useState(false)
  const [notifyWarn, setNotifyWarn] = useState(false)
  const [notifyError, setNotifyError] = useState(true)
  const [enabled, setEnabled] = useState(true)

  useEffect(() => {
    if (isOpen) {
      if (editingSubscription) {
        setEmail(editingSubscription.email)
        setNotifyInfo(editingSubscription.notify_info)
        setNotifyWarn(editingSubscription.notify_warn)
        setNotifyError(editingSubscription.notify_error)
        setEnabled(editingSubscription.enabled)
      } else {
        setEmail('')
        setNotifyInfo(false)
        setNotifyWarn(false)
        setNotifyError(true)
        setEnabled(true)
      }
    }
  }, [isOpen, editingSubscription])

  if (!isOpen) return null

  const isEditing = editingSubscription !== null
  const canSubmit = email.trim().length > 0 && !saving

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    onSave({
      email: email.trim(),
      notify_info: notifyInfo,
      notify_warn: notifyWarn,
      notify_error: notifyError,
      enabled,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold">
            {isEditing
              ? t('subscriptions.editTitle')
              : t('subscriptions.addTitle')}
          </h3>
          <button
            onClick={onCancel}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('subscriptions.email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isEditing}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="user@example.com"
            />
            {isEditing && (
              <p className="mt-1 text-xs text-gray-500">
                {t('subscriptions.emailReadonly')}
              </p>
            )}
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t('subscriptions.levels')}
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={notifyInfo}
                  onChange={(e) => setNotifyInfo(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>
                  <span className="font-medium text-blue-700">INFO</span>
                  <span className="ml-2 text-gray-600">
                    {t('subscriptions.levelInfoDesc')}
                  </span>
                </span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={notifyWarn}
                  onChange={(e) => setNotifyWarn(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>
                  <span className="font-medium text-yellow-700">WARN</span>
                  <span className="ml-2 text-gray-600">
                    {t('subscriptions.levelWarnDesc')}
                  </span>
                </span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={notifyError}
                  onChange={(e) => setNotifyError(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>
                  <span className="font-medium text-red-700">ERROR</span>
                  <span className="ml-2 text-gray-600">
                    {t('subscriptions.levelErrorDesc')}
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="font-medium text-gray-700">
                {t('subscriptions.enabled')}
              </span>
            </label>
          </div>

          {error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
            >
              {t('subscriptions.cancel')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {saving && <LoadingSpinner size="sm" />}
              {saving ? t('subscriptions.saving') : t('subscriptions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
